import { DotsThreeIcon } from '@phosphor-icons/react';
import { VText } from '@vake/ui-kit';
import { formatRelative } from 'date-fns';
import React, { FC } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getRequestHeaders, vake_api_server_url } from '../requests/helpers';
import { useIsAdmin, useSendToMattermost } from '../requests/reports';
import { formatStartEnd } from '../shared/components/time';
import { getOverwatch2Url } from '../utils/env';
import { Loading } from './loading/loading';

interface OverWatchReport {
    start_time: string;
    end_time: string;
    description: string;
    created_at: string;
    uuid: string;
    url: string;
    name: string;
}

const useGetAllSubscriptions = () => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/dashboard/get-overwatch-subscriptions`;
    return useQuery<OverWatchReport[]>(['overwatch_reports'], async () => {
        const res = await fetch(url, {
            credentials: 'include',
            headers: getRequestHeaders(token),
        });

        if (!res.ok) {
            throw new Error(res.statusText);
        }

        return await res.json();
    });
};

type Props = {
    reports?: OverWatchReport[];
    isAdmin?: boolean;
};

const ReportsLoaded: FC<Props> = ({ reports, isAdmin }) => {
    const { mutate } = useSendToMattermost();
    const headers = ['Name', ...(isAdmin ? ['Customer Group'] : []), 'Created', 'Start - End'];
    const overwatchRoot = getOverwatch2Url();

    return (
        <div className="flex flex-col items-center pt-16 gap-16 max-w-[1536px] mx-auto">
            <div className="flex flex-col gap-7">
                <div className="grid grid-cols-lmmmmss">
                    {headers.map((header) => (
                        <VText significance="tertiary" key={header} variant="D1B">
                            {header}
                        </VText>
                    ))}
                </div>
                <div className="flex flex-col gap-2 text-label-primary">
                    {reports &&
                        reports.map((report) => (
                            <Link
                                to={`${overwatchRoot}/${report.uuid}`}
                                className="grid grid-cols-lmmmmss hover:bg-background-primary-hover p-4"
                                key={report.uuid}
                                onClick={() =>
                                    mutate(
                                        `User opened Overwatch [report](${overwatchRoot}/${report.uuid}): ${report.description}`
                                    )
                                }
                            >
                                <VText variant="B2">{report.description}</VText>
                                <VText variant="B2">{report.name}</VText>
                                <VText variant="B2">{formatRelative(new Date(report.created_at), new Date())}</VText>
                                <VText variant="B2">{formatStartEnd(report.start_time, report.end_time)}</VText>
                                <button className="flex items-center justify-end">
                                    <DotsThreeIcon />
                                </button>
                            </Link>
                        ))}
                </div>
            </div>
        </div>
    );
};

const OverwatchReports: React.FC = () => {
    const { data: overWatchReportItems, isLoading, isError } = useGetAllSubscriptions();
    const { token } = useAuth();
    const { data: isAdmin } = useIsAdmin(token);
    if (isError) {
        return (
            <div className="flex items-center justify-center h-screen w-fill bg-background-primary">
                <VText>Fetching data failed</VText>
            </div>
        );
    }
    if (isLoading) {
        return <Loading></Loading>;
    }
    return <ReportsLoaded reports={overWatchReportItems} isAdmin={isAdmin}></ReportsLoaded>;
};

export default OverwatchReports;
